import { z } from "zod";
import { MODES, OPS, type Mode } from "../content/modes";

/*
  The Phase 1 (and prototype) save shape, kept only to read old saves and
  migrate them to the current version. Don't change it.
*/

const SAVE_VERSION = 1;

const int = z.number().int().nonnegative();

const OpsSchema = z.object({
  add: z.boolean().catch(true),
  sub: z.boolean().catch(true),
  mul: z.boolean().catch(false),
  div: z.boolean().catch(false),
});

const SettingsSchema = z.object({
  minutes: z.number().positive().catch(3),
  addMax: z.number().int().positive().catch(20),
  tableMax: z.number().int().positive().catch(10),
  ops: OpsSchema.catch({ add: true, sub: true, mul: false, div: false }),
});
type Settings = z.infer<typeof SettingsSchema>;

const HistoryEntrySchema = z.object({
  /** ISO date, YYYY-MM-DD. */
  d: z.string(),
  mode: z.enum(MODES),
  score: int,
  tried: int,
  mins: z.number().positive(),
});
type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

const ZERO_BEST: Record<Mode, number> = { add: 0, sub: 0, mul: 0, div: 0, mix: 0 };

const BestSchema = z.object({
  add: int.catch(0),
  sub: int.catch(0),
  mul: int.catch(0),
  div: int.catch(0),
  mix: int.catch(0),
});

const SaveSchema = z.object({
  version: z.literal(SAVE_VERSION).catch(SAVE_VERSION),
  name: z.string().max(20).catch(""),
  settings: SettingsSchema.catch({
    minutes: 3,
    addMax: 20,
    tableMax: 10,
    ops: { add: true, sub: true, mul: false, div: false },
  }),
  /** Best score (correct answers in one mission) per mode. */
  best: BestSchema.catch(ZERO_BEST),
  mode: z.enum(MODES).catch("add"),
  /** Most recent first, capped at HISTORY_LIMIT. */
  history: z
    .array(HistoryEntrySchema.catch(null as never))
    .catch([])
    .transform((h) => h.filter(Boolean)),
  /** unlocks[mode][rankIndex] = ISO date the rank was first reached. */
  unlocks: z.partialRecord(z.enum(MODES), z.record(z.string(), z.string())).catch({}),
  /** Missions completed per mode. */
  played: z.partialRecord(z.enum(MODES), int).catch({}),
});

export type SaveV1 = {
  version: typeof SAVE_VERSION;
  name: string;
  settings: Settings;
  best: Record<Mode, number>;
  mode: Mode;
  history: HistoryEntry[];
  unlocks: Partial<Record<Mode, Record<string, string>>>;
  played: Partial<Record<Mode, number>>;
};

export function defaultSaveV1(): SaveV1 {
  return {
    version: SAVE_VERSION,
    name: "",
    settings: {
      minutes: 3,
      addMax: 20,
      tableMax: 10,
      ops: { add: true, sub: true, mul: false, div: false },
    },
    best: { add: 0, sub: 0, mul: 0, div: 0, mix: 0 },
    mode: "add",
    history: [],
    unlocks: {},
    played: {},
  };
}

/**
 * Turn anything (an old prototype save, a partial object, junk) into a valid
 * v1 save. Unknown or broken fields fall back to defaults; good fields are
 * kept. Never throws.
 */
export function coerceSaveV1(raw: unknown): SaveV1 {
  const source = raw && typeof raw === "object" ? raw : {};
  const parsed: SaveV1 = SaveSchema.parse(source);
  const ops = { ...parsed.settings.ops };
  if (!OPS.some((k) => ops[k])) ops.add = true;
  return { ...parsed, settings: { ...parsed.settings, ops } };
}
